/**
 * Keeping the Voice SDK's access token alive, and getting a new one when it is
 * already too late.
 *
 * This is a small file about a large failure. A `Device` holds one JWT, the API
 * signs it for an hour, and when it lapses Twilio does not merely refuse new
 * calls: it refuses the signalling websocket itself. The SDK reconnects, gets
 * 20101 (AccessTokenInvalid) back, and from then on every message it tries to
 * send fails with 31009 ("no transport available"). Meanwhile the WebRTC media
 * is untouched - it does not go through that socket - so a call that is up
 * stays audible in both directions while the browser has quietly lost the
 * ability to hang it up, to be told the other side hung up, or to be rung at
 * all. That is what production reported on 2026-08-11.
 *
 * Two things go wrong with leaving this to the SDK alone:
 *
 *  1. THE WARNING IS TEN SECONDS, not the few minutes it reads like. The SDK's
 *     `tokenWillExpire` fires at `ttl - tokenRefreshMs`, and `tokenRefreshMs`
 *     DEFAULTS TO 10000. Ten seconds is not a budget for an authenticated round
 *     trip, and it is nothing at all in a background tab, where the browser is
 *     free to hold a timer back by a minute - by which point the token this was
 *     supposed to replace has already expired. `TOKEN_REFRESH_LEAD_MS` below is
 *     what the Device is built with instead.
 *  2. NOTHING RETRIES AFTER THE FACT. The SDK raises the event once. If the
 *     fetch fails - a deploy restarting the API is enough - the token dies and
 *     the SDK never asks again, because the next thing it does is emit errors.
 *     `createTokenRenewer` is what turns those errors back into an attempt.
 *
 * Kept out of `useVoiceDevice` so the retry, the cooldown and the ladder can be
 * tested without a browser, a Device or a network.
 */

/**
 * How long before expiry to ask for the next token.
 *
 * Five minutes rather than the SDK's ten seconds. Long enough to survive a
 * throttled background timer and a restarting API, short enough that the token
 * being replaced still has most of its life left if the first attempt lands.
 */
export const TOKEN_REFRESH_LEAD_MS = 5 * 60 * 1000;

/**
 * Twilio error codes that a NEW TOKEN can fix.
 *
 * Bounded by what the SDK can actually hand us, which is narrower than Twilio's
 * own list: only codes present in the SDK's `errorsByCode` map arrive at
 * `device.on("error")` under their own number, and a gateway code with no entry
 * there is re-emitted as the generic ConnectionError (53000). That is why
 * 20105 (AccessTokenNotYetValid) is absent - it can never match, and a signer
 * whose clock runs ahead of Twilio's would not be fixed by asking it again.
 * 31204 and 31205 need no entry either: the SDK rewrites them into the two
 * below before they reach us.
 *
 * Deliberately narrow beyond that. 20103 (issuer/subject), 20106 (grants) and
 * 20107 (signature) are all "this token is wrong" too, but wrong in a way the
 * same API key would produce again - a region that cannot verify the key, a
 * TwiML app that is not there. Re-fetching for those would be an endless loop
 * against our own endpoint, so they are left to the error handler to surface.
 */
const STALE_TOKEN_CODES = new Set([
  /** AccessTokenInvalid. */
  20101,
  /** AccessTokenExpired - the expected one, and the one this file exists for. */
  20104,
]);

/** Would a new token plausibly fix this error? */
export function isStaleTokenError(code: number | undefined): boolean {
  return code !== undefined && STALE_TOKEN_CODES.has(code);
}

/** Doubling from a second, capped at half a minute. */
const RETRY_MS: readonly number[] = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000];

/**
 * Shortest gap between two renewals.
 *
 * The error path is a storm, not an event: a dead transport re-emits 31009 and
 * 20101 every time the SDK retries the socket, several times a second. Without
 * this, one lapsed token would turn into a flood of token requests.
 */
const COOLDOWN_MS = 15_000;

export interface TokenRenewerOptions {
  /** Ask the API for a fresh token. Rejects like any other request. */
  fetch: () => Promise<string>;
  /** Hand it to the Device. Throws when the Device is already destroyed. */
  apply: (token: string) => void;
  /** False once the caller has moved on - stops the ladder mid-retry. */
  alive: () => boolean;
  /** Injected so the test does not have to wait for real seconds. */
  now?: () => number;
  wait?: (ms: number) => Promise<void>;
  retryMs?: readonly number[];
  cooldownMs?: number;
}

/**
 * One renewer per Device: a function that fetches a token, hands it over, and
 * keeps trying if it cannot.
 *
 * Serialised and rate limited. Calling it while an attempt is running joins
 * that attempt rather than starting a second one, and calling it again just
 * after a SUCCESSFUL one does nothing - which is what makes it safe to wire
 * straight to an error event that fires in bursts. A failed attempt does not
 * start the cooldown: the next error is a reason to try again, not to wait.
 *
 * Resolves true when the Device is holding a new token.
 */
export function createTokenRenewer(opts: TokenRenewerOptions): () => Promise<boolean> {
  const now = opts.now ?? (() => Date.now());
  const wait = opts.wait ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const ladder = opts.retryMs ?? RETRY_MS;
  const cooldown = opts.cooldownMs ?? COOLDOWN_MS;

  let inFlight: Promise<boolean> | null = null;
  let lastSuccessAt = 0;

  async function attempt(): Promise<boolean> {
    for (const pause of ladder) {
      if (!opts.alive()) return false;
      try {
        const token = await opts.fetch();
        if (!opts.alive()) return false;
        opts.apply(token);
        lastSuccessAt = now();
        return true;
      } catch {
        // Both failures land here on purpose. A rejected fetch is worth another
        // go; an `apply` that throws means the Device is gone, and `alive()` at
        // the top of the next turn is what stops the ladder for that one.
        await wait(pause);
      }
    }
    return false;
  }

  return function renew(): Promise<boolean> {
    if (inFlight) return inFlight;
    if (lastSuccessAt && now() - lastSuccessAt < cooldown) return Promise.resolve(true);
    inFlight = attempt().finally(() => {
      inFlight = null;
    });
    return inFlight;
  };
}
